import { useCallback, useEffect, useId, useMemo, useRef, useState, type ElementType, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import {
  Search, X, FileText, FolderKanban, FolderOpen, MessageSquare, Clock, Building2, Contact,
  Target, Receipt, Briefcase, Milestone, CornerDownLeft, History, type LucideIcon,
} from 'lucide-react';
import { api } from '../services/api';
import type { SearchResult, SearchResultType } from '../types';
import { useAuthStore } from '../store/authStore';
import Dialog from './ui/Dialog';
import { useNavGroups } from './navigation';
import { usePermissions } from '../hooks/usePermissions';
import { runQuickCreate, useQuickCreateItems } from './QuickCreate';

// ============================================================================
// Command palette v2 (T-34, PRD §4.5). One instance in the shell; the top-bar
// field, the mobile search button and Ctrl/⌘+K open it. Floating glass layer
// (`mat-float`) over an unblurred scrim. ARIA 1.2 combobox + listbox.
//
// Sections: אחרונים (last 8 opened results) · פעולות (QuickCreate + new
// customer / quote) · מעבר אל (the nav model, so destinations = sidebar) ·
// results grouped by type. A leading ">" lists actions only. Search scope is
// enforced server-side; each result carries its route.
// ============================================================================

interface PaletteState { open: boolean; setOpen: (open: boolean) => void; toggle: () => void }
export const useCommandPalette = create<PaletteState>((set, get) => ({
  open: false,
  setOpen: (open) => set({ open }),
  toggle: () => set({ open: !get().open }),
}));

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
export const PALETTE_SHORTCUT = isMac ? '⌘K' : 'Ctrl\u00A0K';

/**
 * The server returns snippets that are already HTML-escaped, with <mark>
 * around matches. Decode each text segment and re-escape it once, keeping
 * only the <mark> tags, so nothing else can render as markup and entities
 * are not escaped twice.
 */
function sanitizeHighlight(html: string): string {
  const decoder = document.createElement('textarea');
  const encoder = document.createElement('div');
  return html
    .split(/(<\/?mark>)/)
    .map((part) => {
      if (part === '<mark>' || part === '</mark>') return part;
      decoder.innerHTML = part;
      encoder.textContent = decoder.value;
      return encoder.innerHTML;
    })
    .join('');
}

// Entity icons are monochrome (PRD §4.5); the type is said in words by the group heading.
const TYPE_CONFIG: Record<SearchResultType, { labelKey: string; icon: LucideIcon }> = {
  ticket: { labelKey: 'search.tickets', icon: FileText },
  project: { labelKey: 'search.projects', icon: FolderKanban },
  milestone: { labelKey: 'search.milestones', icon: Milestone },
  customer: { labelKey: 'search.customers', icon: Building2 },
  contact: { labelKey: 'search.contacts', icon: Contact },
  lead: { labelKey: 'search.leads', icon: Target },
  quote: { labelKey: 'search.quotes', icon: Receipt },
  engagement: { labelKey: 'search.engagements', icon: Briefcase },
  comment: { labelKey: 'search.comments', icon: MessageSquare },
  worklog: { labelKey: 'search.workLogs', icon: Clock },
  document: { labelKey: 'search.documents', icon: FolderOpen },
};

const EMPTY_TOTALS = Object.fromEntries(Object.keys(TYPE_CONFIG).map((k) => [k, 0])) as Record<SearchResultType, number>;

function getNavigationPath(result: SearchResult): string {
  if (result.url) return result.url;
  switch (result.type) {
    case 'ticket':
    case 'comment':
    case 'worklog':
      return `/tickets/${result.id}`;
    case 'project':
      return `/projects/${result.id}`;
    case 'milestone':
      return `/milestones/${result.id}`;
    case 'customer':
      return `/organizations/${result.id}`;
    case 'lead':
      return `/leads/${result.id}`;
    case 'quote':
      return `/quotes/${result.id}`;
    case 'engagement':
      return `/engagements/${result.id}`;
    case 'document':
      return '/documents';
    default:
      return '/';
  }
}

// ─── Recent (last 8 opened results, this device) ─────────────────────────────

interface Recent { type: SearchResultType; id: string; title: string; path: string }
// DQ-443: per user (a shared computer must not show one employee's recents to the next one); removed on sign-out (authStore.clearAuth).
const RECENT_KEY_PREFIX = 'ote_palette_recent';
const recentKey = () => `${RECENT_KEY_PREFIX}:${useAuthStore.getState().user?.id ?? 'anon'}`;

function readRecent(): Recent[] {
  try {
    const list = JSON.parse(localStorage.getItem(recentKey()) || '[]');
    return Array.isArray(list) ? list.filter((r) => r && r.path && r.title).slice(0, 8) : [];
  } catch {
    return [];
  }
}

function pushRecent(r: Recent) {
  try {
    const next = [r, ...readRecent().filter((x) => x.path !== r.path)].slice(0, 8);
    localStorage.setItem(recentKey(), JSON.stringify(next));
  } catch {
    // storage unavailable: recents are a convenience only
  }
}

// ─── Triggers ────────────────────────────────────────────────────────────────

/**
 * The top-bar search field (desktop) or icon (mobile). Its accessible name is
 * "חיפוש גלובלי" / "Global search" (kept for tests); the visible word "חיפוש"
 * is part of it (WCAG 2.5.3).
 */
export function SearchTrigger({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const open = useCommandPalette((s) => s.open);
  // FIX-B15 (QA-P33bc): an internal-only employee has no search (/search is closed to her).
  const { internalOnly } = usePermissions();
  const onClick = () => useCommandPalette.getState().setOpen(true);
  if (internalOnly) return null;
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={t('aria.globalSearch')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="btn-icon text-text-2 hover:bg-surface-subtle hover:text-text transition-colors duration-micro"
      >
        <Search size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('aria.globalSearch')}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-keyshortcuts="Control+K Meta+K"
      className="flex h-control w-80 max-w-full items-center gap-2 rounded-lg bg-surface-subtle px-3 text-sm text-text-2 transition-colors duration-micro hover:bg-surface-200"
    >
      <Search size={16} strokeWidth={1.75} aria-hidden="true" />
      <span className="flex-1 text-start">{t('shell.search.field')}</span>
      {/* The shortcut is drawn by CSS (data-shortcut), so the button's visible text
          stays "חיפוש", which its name contains (WCAG 2.5.3, axe label-content-name-mismatch). */}
      <kbd
        dir="ltr"
        data-shortcut={PALETTE_SHORTCUT}
        className="rounded-xs border border-border bg-surface px-1.5 font-sans text-xs leading-5 text-text-2 after:content-[attr(data-shortcut)]"
        aria-hidden="true"
      />
    </button>
  );
}

// ─── Palette ─────────────────────────────────────────────────────────────────

interface Option {
  key: string;
  label: string;
  icon: ElementType;
  run: () => void;
  /** Result snippet (sanitized HTML with <mark>). */
  highlight?: string;
  sub?: string;
  /** C-09 (clarity, A-22): synonyms the option also answers to (lower-case). */
  keywords?: string[];
}

interface Section { key: string; label: string; options: Option[] }

/** Mounted once in the shell. Default export kept for the old import name. */
/** FIX-B15 (QA-P33bc): no palette (and no Ctrl+K) for an internal-only employee. */
export default function CommandPalette() {
  const { internalOnly } = usePermissions();
  return internalOnly ? null : <CommandPaletteInner />;
}

function CommandPaletteInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = useCommandPalette((s) => s.open);
  const setOpen = useCommandPalette((s) => s.setOpen);
  const navGroups = useNavGroups();
  const quickItems = useQuickCreateItems();
  // Destinations come from the nav (already filtered); actions, recents and
  // results that lead into a closed screen are left out too.
  const { canPath } = usePermissions();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totals, setTotals] = useState<Record<SearchResultType, number>>(EMPTY_TOTALS);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<Recent[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const baseId = useId();
  const listId = `${baseId}-list`;

  // Ctrl/⌘+K anywhere toggles the palette (one listener, one instance).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key.toLowerCase() === 'k' || e.code === 'KeyK')) {
        e.preventDefault();
        useCommandPalette.getState().toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setRecent(readRecent());
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuery('');
    setResults([]);
    setTotals(EMPTY_TOTALS);
    setSearchError(false);
    setLoading(false);
    setActive(0);
  }, [open]);

  const go = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
  }, [navigate, setOpen]);

  const actionsOnly = query.startsWith('>');
  const filter = (actionsOnly ? query.slice(1) : query).trim().toLowerCase();
  const searching = !actionsOnly && filter.length >= 2;

  // C-09 (clarity, A-22): a destination or action also answers to its synonyms
  // (`search.synonyms.<nav id>`, `search.synonymsAction.<action>`: comma-separated, per language).
  const synonymsOf = useCallback((key: string): string[] =>
    String(t(key, { defaultValue: '' })).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean), [t]);

  // C-09 (clarity, A-12): the SAME list as the floating "+" (`useQuickCreateItems`), nothing added here.
  const actions: Option[] = useMemo(() => quickItems.map((q) => ({
    key: `qc:${q.id}`,
    label: q.label,
    icon: q.icon,
    keywords: synonymsOf(`search.synonymsAction.${q.id}`),
    run: () => { setOpen(false); requestAnimationFrame(() => runQuickCreate(q.id)); },
  })), [quickItems, synonymsOf, setOpen]);

  const destinations: Option[] = useMemo(() => navGroups.flatMap((g) => g.items.map((i) => ({
    key: `nav:${i.to}`,
    label: i.label,
    icon: i.icon,
    sub: g.label || undefined,
    keywords: synonymsOf(`search.synonyms.${i.id}`),
    run: () => go(i.to),
  }))), [navGroups, synonymsOf, go]);

  const sections: Section[] = useMemo(() => {
    const match = (o: Option) => !filter || o.label.toLowerCase().includes(filter)
      || (o.keywords ?? []).some((k) => k.includes(filter) || filter.includes(k));
    const out: Section[] = [];
    const openRecent = recent.filter((r) => canPath(r.path));
    if (!actionsOnly && !filter && openRecent.length) {
      out.push({
        key: 'recent',
        label: t('shell.palette.recent'),
        options: openRecent.map((r) => ({
          key: `recent:${r.path}`,
          label: r.title,
          icon: TYPE_CONFIG[r.type]?.icon ?? History,
          sub: t(TYPE_CONFIG[r.type]?.labelKey ?? 'search.tickets'),
          run: () => go(r.path),
        })),
      });
    }
    const acts = actions.filter(match);
    if (acts.length) out.push({ key: 'actions', label: t('shell.palette.actions'), options: searching ? acts.slice(0, 3) : acts });
    if (!actionsOnly) {
      const dests = destinations.filter(match);
      if (dests.length) out.push({ key: 'nav', label: t('shell.palette.goTo'), options: searching ? dests.slice(0, 4) : dests });
    }
    if (searching && !loading && !searchError) {
      const grouped = new Map<SearchResultType, SearchResult[]>();
      for (const r of results) {
        if (canPath(getNavigationPath(r))) grouped.set(r.type, [...(grouped.get(r.type) ?? []), r]);
      }
      for (const [type, items] of grouped) {
        const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.ticket;
        out.push({
          key: `results:${type}`,
          label: `${t(cfg.labelKey)} (${totals[type] ?? items.length})`,
          options: items.map((r, i) => ({
            key: `r:${r.type}:${r.id}:${i}`,
            label: r.title,
            icon: cfg.icon,
            highlight: r.highlight ? sanitizeHighlight(r.highlight) : undefined,
            sub: r.breadcrumb || undefined,
            run: () => {
              const path = getNavigationPath(r);
              pushRecent({ type: r.type, id: r.id, title: r.title, path });
              go(path);
            },
          })),
        });
      }
    }
    return out;
  }, [filter, actionsOnly, searching, recent, actions, destinations, results, totals, loading, searchError, canPath, t, go]);

  const flat = useMemo(() => sections.flatMap((s) => s.options), [sections]);
  const activeIndex = flat.length ? Math.min(active, flat.length - 1) : -1;
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  const doSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2 || q.startsWith('>')) {
      setResults([]);
      setTotals(EMPTY_TOTALS);
      setSearchError(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    try {
      const res = await api.search({ q: trimmed });
      if (res.data) {
        setResults(res.data.results);
        setTotals({ ...EMPTY_TOTALS, ...res.data.totals });
        setActive(0);
      }
    } catch {
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setActive(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (flat.length ? (Math.min(i, flat.length - 1) + 1) % flat.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (flat.length ? (Math.min(i, flat.length - 1) - 1 + flat.length) % flat.length : 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      flat[activeIndex].run();
    }
  };

  // Keep the active option in view.
  useEffect(() => {
    if (activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const onListClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (!el) return;
    const i = Number(el.dataset.index);
    if (flat[i]) flat[i].run();
  };
  const onListMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (el) setActive(Number(el.dataset.index));
  };

  const resultCount = useMemo(() => results.filter((r) => canPath(getNavigationPath(r))).length, [results, canPath]);
  const status = searching
    ? loading ? t('search.searching')
      : searchError ? t('shell.palette.error')
        : resultCount === 0 ? t('shell.palette.noResults') : t('shell.palette.results', { count: resultCount })
    : '';

  let index = -1;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      title={t('aria.globalSearch')}
      hideHeader
      position="top"
      material="float"
      sheetOnMobile={false}
      className="max-w-palette"
      bodyClassName="flex flex-col p-0"
      initialFocus={inputRef}
    >
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-border px-4">
        <Search size={18} strokeWidth={1.75} className="flex-shrink-0 text-text-2" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={flat.length > 0}
          aria-controls={listId}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-autocomplete="list"
          aria-label={t('shell.palette.placeholder')}
          aria-describedby={`${baseId}-hint`}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('shell.palette.placeholder')}
          autoComplete="off"
          spellCheck={false}
          className="h-14 min-w-0 flex-1 bg-transparent text-base text-text placeholder:text-text-2 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => { handleQueryChange(''); inputRef.current?.focus(); }}
            className="btn-icon text-text-2 hover:bg-surface-subtle hover:text-text"
            aria-label={t('aria.clearSearch')}
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        )}
      </div>

      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={t('shell.palette.listLabel')}
        tabIndex={-1}
        onClick={onListClick}
        onMouseMove={onListMove}
        onKeyDown={handleKeyDown}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5 focus:outline-none"
      >
        {sections.map((section) => (
          <div key={section.key} role="group" aria-labelledby={`${baseId}-${section.key}`} className="pb-1">
            <div id={`${baseId}-${section.key}`} className="px-2.5 pb-1 pt-2 text-xs font-medium text-text-2" role="presentation">
              {section.label}
            </div>
            {section.options.map((o) => {
              index += 1;
              const i = index;
              const Icon = o.icon;
              const selected = i === activeIndex;
              return (
                <div
                  key={o.key}
                  id={optionId(i)}
                  role="option"
                  aria-selected={selected}
                  data-index={i}
                  className={`flex cursor-default items-center gap-3 rounded-lg px-2.5 py-2 ${selected ? 'bg-accent-soft' : ''}`}
                >
                  <span className="flex size-7 flex-shrink-0 items-center justify-center rounded-md bg-surface-subtle text-text-3" aria-hidden="true">
                    <Icon size={16} strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm ${selected ? 'text-accent-soft-text font-medium' : 'text-text'}`}>{o.label}</span>
                    {/* DQ-444: the highlight only when it adds something to the title; the context (customer / project) always shows. */}
                    {o.highlight && o.highlight.replace(/<[^>]+>/g, '').trim() !== String(o.label).trim() && (
                      <span
                        className="block truncate text-xs text-text-2 [&_mark]:bg-transparent [&_mark]:font-semibold [&_mark]:text-text"
                        dangerouslySetInnerHTML={{ __html: o.highlight }}
                      />
                    )}
                    {o.sub && <span className="block truncate text-xs text-text-2">{o.sub}</span>}
                  </span>
                  {selected && <CornerDownLeft size={14} strokeWidth={1.75} className="flex-shrink-0 text-text-2 rtl-mirror" aria-hidden="true" />}
                </div>
              );
            })}
          </div>
        ))}

        {searching && (loading || searchError || resultCount === 0) && (
          <p className={`px-3 py-6 text-center text-sm ${searchError ? 'text-danger-700' : 'text-text-2'}`}>{status}</p>
        )}

      </div>

      <div className="hidden flex-shrink-0 items-center gap-4 border-t border-border px-4 py-2 text-xs text-text-2 sm:flex" aria-hidden="true">
        <span className="flex items-center gap-1.5"><kbd className="font-sans">↑↓</kbd>{t('shell.palette.keyMove')}</span>
        <span className="flex items-center gap-1.5"><kbd className="font-sans">↵</kbd>{t('shell.palette.keySelect')}</span>
        <span className="flex items-center gap-1.5"><kbd className="font-sans">Esc</kbd>{t('shell.palette.keyClose')}</span>
        <span className="ms-auto"><kbd dir="ltr" className="font-sans">{PALETTE_SHORTCUT}</kbd></span>
      </div>

      <span id={`${baseId}-hint`} className="sr-only">{t('shell.palette.hint')}</span>
      <span className="sr-only" role="status" aria-live="polite">{status}</span>
    </Dialog>
  );
}
